/**
 * The timezones somebody may choose.
 *
 * This was a text field until 19 September 2026, and a text field is the wrong
 * control for it twice over. A zone name is not something anybody remembers
 * exactly — "Asia/Baku" is easy, "America/Argentina/Buenos_Aires" is not — and a
 * mistyped one does not fail loudly: org.Branch.Location falls back to UTC, so a
 * branch with "Asia/Bakü" or "asia/baku" goes on serving customers while dating
 * every ticket on the wrong business day. The fault shows up a month later in a
 * report nobody can reconcile.
 *
 * The list comes from the browser rather than from the server, and that is worth
 * saying out loud because the server is the one that decides. Intl.supportedValuesOf
 * is the same IANA database, kept current by the browser vendor rather than by us,
 * and it costs no endpoint and no round trip. Where the two disagree — an exotic
 * zone one knows and the other does not — the server refuses the save and says so
 * in words, which is a far better failure than the silent UTC fallback this
 * replaces.
 */

/** Zones offered first, because this product is sold in Azerbaijan and its
 *  neighbours, and a list of four hundred that opens on Africa/Abidjan makes
 *  somebody scroll to find the only answer they were ever going to give. */
const LIKELY = [
  'Asia/Baku',
  'Asia/Tbilisi',
  'Asia/Yerevan',
  'Europe/Istanbul',
  'Europe/Moscow',
  'Asia/Dubai',
  'Europe/London',
  'UTC',
]

/** Used where the browser is too old to enumerate zones itself. Short on purpose:
 *  it is a fallback for a browser nobody in a bank is running, not a second
 *  catalogue to keep in step with the first. */
const FALLBACK = LIKELY

export interface TimezoneOption {
  value: string
  label: string
}

/**
 * The options for a timezone field, with the likely ones first.
 *
 * `current` is included even when the browser has never heard of it. A branch
 * configured years ago against a zone that has since been renamed must not have it
 * silently swapped for the first entry in the list the moment somebody opens the
 * form to change its name.
 */
export function timezoneOptions(current?: string): TimezoneOption[] {
  const all = supported()
  const seen = new Set<string>()
  const out: TimezoneOption[] = []

  const add = (zone: string) => {
    if (!zone || seen.has(zone)) return
    seen.add(zone)
    out.push({ value: zone, label: labelFor(zone) })
  }

  if (current && !all.includes(current)) add(current)
  for (const zone of LIKELY) if (all.includes(zone)) add(zone)
  for (const zone of all) add(zone)
  return out
}

/** The zone name with its current offset, because "Asia/Baku" and "Asia/Tbilisi"
 *  are indistinguishable to somebody who is checking they picked the right one and
 *  "+04:00" is what they are actually checking. */
function labelFor(zone: string): string {
  const offset = offsetOf(zone)
  return offset ? `${zone} — ${offset}` : zone
}

function offsetOf(zone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en', {
      timeZone: zone,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date())
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? ''
  } catch {
    // An unknown zone, which is the one case this must not throw on: it is
    // reached by the branch whose stored zone the browser does not recognise,
    // and that row still has to be listed so it can be corrected.
    return ''
  }
}

function supported(): string[] {
  try {
    const supportedValuesOf = (
      Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
    ).supportedValuesOf
    const zones = supportedValuesOf?.('timeZone')
    if (zones && zones.length > 0) return zones
  } catch {
    // Falls through to the short list below.
  }
  return FALLBACK
}
